import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTarih, formatTl, waLink } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from './Ipucu';
import { hashaKaydir } from './kaydir';
import { TahsilCekmece, useKasaHesaplari } from './KasaHesapSec';
import { sor } from './Sor';

/**
 * SİPARİŞ VADELERİ — siparişlerin ödeme planından vadesi geçen (kırmızı),
 * bugün dolan ve 14 gün içinde dolacak parçalar. Tahsilat ekranındaki bölüm ve
 * göstergedeki kırmızı kart aynı ucu okur (`/api/siparis-vadeleri`).
 */
export type VadeSatiri = {
  paymentId: number; orderId: number; no: string; title: string; statusLabel: string;
  cari: { id: number; name: string; phone: string | null };
  etiket: string; vadeAd: string | null;
  tutarKurus: number; kalanKurus: number; vadeTarihi: string | null; gecenGun: number | null;
  grup: 'gecmis' | 'bugun' | 'yaklasan';
};
type Kutu = { adet: number; tutarKurus: number };
export type VadeVerisi = {
  satirlar: VadeSatiri[];
  ozet: { gecmis: Kutu; bugun: Kutu; yaklasan: Kutu; ileri: Kutu; baslamadi: Kutu };
};

export function useSiparisVadeleri(acik: boolean) {
  const [veri, setVeri] = useState<VadeVerisi | null>(null);
  const yukle = useCallback(() => {
    if (!acik) return;
    api<VadeVerisi>('/api/siparis-vadeleri').then(setVeri).catch(() => undefined);
  }, [acik]);
  useEffect(yukle, [yukle]);
  return { veri, yukle };
}

const GRUP_AD = { gecmis: 'Vadesi geçen', bugun: 'Bugün', yaklasan: 'Yaklaşan 14 gün' } as const;

export default function SiparisVadeleri({ veri, yenile }: { veri: VadeVerisi; yenile: () => void }) {
  const { can } = useAuth();
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<number | null>(null);

  /** Kasaya yazabilen rolde not + "hangi kasaya girdi" çekmecesi açılır; diğerlerinde eski soru penceresi. */
  const kasa = useKasaHesaplari();
  const [tahsil, setTahsil] = useState<VadeSatiri | null>(null);

  async function tahsilEdildi(s: VadeSatiri) {
    if (kasa.acik) { setHata(null); setBilgi(null); setTahsil(s); return; }
    const notu = await sor({
      baslik: 'Tahsil edildi olarak işaretle', alt: `${s.no} · ${s.cari.name}`,
      aciklama: <>{s.etiket} · <b>{formatTl(s.tutarKurus)}</b> tahsil edildi olarak işaretlenecek.</>,
      etiket: 'Not', yerTutucu: 'Havale, çek, nakit…', azami: 300, onay: 'Tahsil edildi',
    });
    if (notu == null) return;
    setHata(null); setBilgi(null); setMesgul(s.paymentId);
    try {
      await api(`/api/siparis/${s.orderId}/odeme/${s.paymentId}`, { method: 'POST', body: JSON.stringify({ odendi: true, not: notu || null }) });
      setBilgi(`${s.no} · ${s.etiket} tahsil edildi olarak işaretlendi.`);
      yenile();
    } catch (e) { setHata(e instanceof Error ? e.message : 'İşaretlenemedi'); }
    finally { setMesgul(null); }
  }

  // Göstergedeki kırmızı kart `/tahsilat#siparis-vadeleri` ile buraya indirir.
  useEffect(() => hashaKaydir('siparis-vadeleri'), []);

  const bekleyen = veri.ozet.ileri.adet + veri.ozet.baslamadi.adet;
  return (
    <div className="ccd-card ccd-stack-sm" id="siparis-vadeleri" data-tur="siparis-vadeleri">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="ccd-fieldset-title">Sipariş vadeleri <Ipucu id="siparis-vade-odemesi" /></div>
          <div className="ccd-meta">
            Siparişlerin ödeme planından: vadesi geçen, bugün dolan ve 14 gün içinde dolacak ödemeler.
            {bekleyen > 0 && ` Ayrıca ${bekleyen} ödeme daha ileri tarihli ya da fatura / sevk bekliyor.`}
          </div>
        </div>
      </div>
      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      {veri.satirlar.length === 0 ? (
        <div className="ccd-empty">Vadesi gelen ya da yaklaşan sipariş ödemesi yok.</div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Sipariş</th><th className="ccd-th">Cari</th><th className="ccd-th">Ödeme <Ipucu id="odeme-plani" /></th>
              <th className="ccd-th ccd-td-num">Tutar</th><th className="ccd-th">Vade <Ipucu id="vade" /></th><th className="ccd-th">Durum</th><th className="ccd-th">İşlem</th>
            </tr></thead>
            <tbody>
              {(['gecmis', 'bugun', 'yaklasan'] as const).flatMap((g) => {
                const grup = veri.satirlar.filter((s) => s.grup === g);
                if (grup.length === 0) return [];
                return [
                  <tr key={g} className="ccd-vade-grup"><td colSpan={7}>{GRUP_AD[g]} · {grup.length} ödeme · {formatTl(grup.reduce((t, s) => t + s.kalanKurus, 0))}</td></tr>,
                  ...grup.map((s) => {
                    const wa = waLink(s.cari.phone, `Merhaba ${s.cari.name},\n${s.no} numaralı siparişinizin ${formatTl(s.kalanKurus)} tutarındaki ödemesini hatırlatmak istedik.\n\nArt Mozaik & Seramik`);
                    return (
                      <tr key={s.paymentId} className={`ccd-tr${g === 'gecmis' ? ' ccd-vade-gecikti' : ''}`}>
                        <td className="ccd-td">
                          <Link to={`/siparis/${s.orderId}`} className="ccd-mono" title="Sipariş detayı"><b>{s.no}</b></Link>
                          <div className="ccd-meta">{s.title}</div>
                        </td>
                        <td className="ccd-td">
                          <Link to={`/crm/${s.cari.id}`} title="Cari kartı ve ekstre"><b>{s.cari.name}</b></Link>
                          {s.cari.phone && <div className="ccd-meta">{s.cari.phone}</div>}
                        </td>
                        <td className="ccd-td">{s.etiket}{s.vadeAd && s.vadeAd !== s.etiket && <div className="ccd-meta">{s.vadeAd}</div>}</td>
                        <td className="ccd-td ccd-td-num">
                          <b>{formatTl(s.kalanKurus)}</b>
                          {s.kalanKurus !== s.tutarKurus && <div className="ccd-meta">{formatTl(s.tutarKurus)} içinden</div>}
                        </td>
                        <td className="ccd-td ccd-td-nowrap">{s.vadeTarihi ? formatTarih(s.vadeTarihi) : '—'}</td>
                        <td className="ccd-td ccd-td-nowrap">
                          {g === 'gecmis' ? <span className="ccd-badge ccd-badge-danger">{s.gecenGun} gün geçti</span>
                            : g === 'bugun' ? <span className="ccd-badge ccd-badge-warning">bugün</span>
                            : <span className="ccd-badge">{-(s.gecenGun ?? 0)} gün var</span>}
                        </td>
                        <td className="ccd-td ccd-td-nowrap">
                          <span className="ccd-row" style={{ gap: 6 }}>
                            {can('order.write') && (
                              <button className="ccd-btn ccd-btn-sm" disabled={mesgul === s.paymentId} onClick={() => void tahsilEdildi(s)}>Tahsil edildi</button>
                            )}
                            {wa && g !== 'yaklasan' && <a className="ccd-btn ccd-btn-ghost ccd-btn-sm" href={wa} target="_blank" rel="noreferrer">WhatsApp</a>}
                          </span>
                        </td>
                      </tr>
                    );
                  }),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
      {tahsil && (
        <TahsilCekmece baslik={`${tahsil.no} · ${tahsil.cari.name}`} altBaslik={tahsil.etiket} tutarKurus={tahsil.tutarKurus} hesaplar={kasa.hesaplar}
                       kapat={() => setTahsil(null)}
                       onayla={async (v) => {
                         const r = await api<{ kasaHareketId: number | null }>(`/api/siparis/${tahsil.orderId}/odeme/${tahsil.paymentId}`, {
                           method: 'POST', body: JSON.stringify({ odendi: true, not: v.not, ...(v.hesapId ? { hesapId: v.hesapId, islemAnahtari: v.islemAnahtari } : {}) }),
                         });
                         setBilgi(`${tahsil.no} · ${tahsil.etiket} tahsil edildi olarak işaretlendi.${r.kasaHareketId ? ' Kasa girişi yazıldı.' : ''}`);
                         yenile();
                       }} />
      )}
    </div>
  );
}
